import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './RecruitmentPlatform.css';
import API_BASE_URL from "../config";
import Management from './Management';
import HRCopilotHero from './HRCopilotHero';
import HRCopilotNewsSection from "./HRCopilotNewsSection";
import HRCopilotJobStatus from './HRCopilotJobStatus';
import HRCopilotPipeline from './HRCopilotPipeline';
import HRCopilotAutoImport from './HRCopilotAutoImport';
import HRCopilotFeatures from './HRCopilotFeatures';
import HRCopilotPrivacySkills from './HRCopilotPrivacySkills';
import HRCopilotLanding from './HRCopilotLanding';
import HRCopilotEraSection from './HRCopilotEraSection';
import HRCopilotRequirementSection from './HRCopilotRequirementSection';
import HRQuoteBanner from './HRCopilotEnterprise';
import AIShareJobSection from "./AIShareJobSection";
import RecuirementWiseCode from './RecuirementWiseCode';
import useSEO from "../utils/useSEO";

const RecruitmentPlatform = () => {
  const [isFeaturesOpen, setIsFeaturesOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVideoPopupOpen, setIsVideoPopupOpen] = useState(false);
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);


  useSEO({
    title: "Recruitment, Staffing & Employee Onboarding | Humanex",
    description:
      "Explore Humanex recruitment, staffing, and employee onboarding services designed to streamline hiring, reduce turnaround time, and improve workforce readiness.",
  });

  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    companyName: '',
    email: '',
    phone: '',
    companySize: '',
    hiringNeeds: '',
    timeline: ''
  });

  // Enhanced scroll and visibility effects
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      setIsVisible(window.scrollY > 100);

      // Scroll progress
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    setIsVisible(true);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % talentFeatures.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const toggleFeatures = () => setIsFeaturesOpen(!isFeaturesOpen);
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const toggleNavbar = () => setIsNavbarOpen(!isNavbarOpen);

  const handleDropdownClick = (path) => {
    navigate(path);
    setIsFeaturesOpen(false);
    setIsMobileMenuOpen(false);
    setIsNavbarOpen(false);
  };

  const handleAccessRecruit = () => navigate('/payment');
  const handleRecruit = () => navigate('/access-recruit');

  const handleSignIn = () => navigate('/hr-portal');

  const openVideoPopup = () => {
    setIsVideoPopupOpen(true);
  };

  const closeVideoPopup = () => {
    setIsVideoPopupOpen(false);
  };

  // Video Popup Component
  const VideoPopup = ({ isOpen, onClose, videoUrl }) => {
    const videoRef = useRef(null);

    useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
        if (videoRef.current) {
          videoRef.current.currentTime = 0;
        }
      } else {
        document.body.style.overflow = 'unset';
      }

      return () => {
        document.body.style.overflow = 'unset';
      };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
      <div className="talent-video-popup-overlay" onClick={onClose}>
        <div className="talent-video-popup-content" onClick={(e) => e.stopPropagation()}>
          <button className="talent-video-popup-close" onClick={onClose}>
            ×
          </button>
          <div className="talent-video-container">
            <video ref={videoRef} controls autoPlay className="talent-demo-video">
              <source src={videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <div className="talent-video-popup-info">
            <h3>Talent Acquisition Platform Walkthrough</h3>
            <p>See how our AI-powered recruitment platform transforms your hiring process and helps you find top talent faster</p>
          </div>
        </div>
      </div>
    );
  };

  // Recruitment-specific features data
  const talentFeatures = [
    {
      icon: 'screening',
      title: 'AI Candidate Screening',
      description: 'Automated resume analysis and candidate matching with 95% accuracy using machine learning algorithms',
      stats: '95% Accuracy'
    },
    {
      icon: 'collaboration',
      title: 'Hiring Team Collaboration',
      description: 'Real-time feedback, interview scheduling, and seamless coordination across your entire hiring team',
      stats: '3x Faster'
    },
    {
      icon: 'pipeline',
      title: 'Smart Talent Pipeline',
      description: 'Automated candidate nurturing and relationship management to build lasting talent communities',
      stats: '65% Time Saved'
    },
    {
      icon: 'analytics',
      title: 'Recruitment Analytics',
      description: 'Data-driven insights on hiring metrics, diversity tracking, and candidate experience optimization',
      stats: '40% Better Hires'
    },
    {
      icon: 'integration',
      title: 'HR Ecosystem Integration',
      description: 'Seamless connectivity with ATS, HRIS, and productivity tools you already use',
      stats: '50+ Integrations'
    },
    {
      icon: 'assessment',
      title: 'Skill Assessment Tools',
      description: 'Built-in technical tests, behavioral assessments, and cultural fit evaluation',
      stats: '100% Customizable'
    }
  ];

  const recruitmentStats = [
    { number: '65%', label: 'Faster Time-to-Hire', suffix: 'average reduction in hiring cycle' },
    { number: '3.2M+', label: 'Candidates Processed', suffix: 'successful talent matches globally' },
    { number: '98.7%', label: 'Client Satisfaction', suffix: 'of companies recommend our platform' },
    { number: '4.8/5', label: 'Candidate Rating', suffix: 'based on 50k+ candidate reviews' }
  ];

  const hiringProcess = [
    {
      phase: "Talent Sourcing",
      title: "Intelligent Candidate Discovery",
      steps: ["Multi-Channel Sourcing", "AI-Powered Matching", "Talent Pool Building", "Passive Candidate Engagement"],
      time: "Stage 1"
    },
    {
      phase: "Candidate Assessment",
      title: "Comprehensive Evaluation",
      steps: ["Skill Validation", "Cultural Fit Analysis", "Behavioral Assessment", "Technical Screening"],
      time: "Stage 2"
    },
    {
      phase: "Selection & Offer",
      title: "Streamlined Decision Making",
      steps: ["Structured Interviews", "Collaborative Feedback", "Offer Management", "Onboarding Preparation"],
      time: "Stage 3"
    }
  ];

  const recruitmentMetrics = [
    { name: "Candidate Sourcing", progress: 92 },
    { name: "Initial Screening", progress: 85 },
    { name: "Technical Assessment", progress: 70 },
    { name: "Interview Process", progress: 65 },
    { name: "Offer Stage", progress: 80 },
    { name: "Onboarding", progress: 60 }
  ];

  const hrIntegrations = [
    'LinkedIn Recruiter', 'Greenhouse', 'Lever', 'Workday', 'BambooHR', 'ADP',
    'Slack', 'Microsoft Teams', 'Zoom', 'Google Workspace', 'Office 365', 'Jira'
  ];

  const TalentBackground = () => (
    <div className="talent-background-animation">
      {/* Floating Shapes */}
      {/* <div className="talent-floating-shape talent-shape-1"></div>
      <div className="talent-floating-shape talent-shape-2"></div>
      <div className="talent-floating-shape talent-shape-3"></div>
      <div className="talent-floating-shape talent-shape-4"></div>
      <div className="talent-floating-shape talent-shape-5"></div> */}

      {/* Animated Gradient Orbs */}
      {/* <div className="talent-gradient-orb talent-orb-1"></div>
      <div className="talent-gradient-orb talent-orb-2"></div>
      <div className="talent-gradient-orb talent-orb-3"></div> */}

      {/* SVG Background Animation */}
      <svg className="talent-bg-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <linearGradient id="talent-gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(74, 144, 226, 0.08)" />
            <stop offset="50%" stopColor="rgba(120, 119, 216, 0.05)" />
            <stop offset="100%" stopColor="rgba(226, 74, 144, 0.08)" />
          </linearGradient>
          <linearGradient id="talent-gradient2" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(120, 119, 216, 0.06)" />
            <stop offset="50%" stopColor="rgba(119, 216, 216, 0.04)" />
            <stop offset="100%" stopColor="rgba(74, 226, 144, 0.06)" />
          </linearGradient>
          <pattern id="talent-grid" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100" height="100" fill="url(#talent-grid)" />
        <path
          d="M0,15 Q30,5 60,25 T100,35 L100,100 L0,100 Z"
          fill="url(#talent-gradient1)"
          className="talent-svg-path-1"
        />
        <path
          d="M0,65 Q40,45 80,70 T100,85 L100,100 L0,100 Z"
          fill="url(#talent-gradient2)"
          opacity="0.8"
          className="talent-svg-path-2"
        />
      </svg>
    </div>
  );

  const TalentFeatureIcon = ({ type }) => {
    const icons = {
      screening: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 12L11 14L15 10" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C13.1974 3 14.3497 3.23385 15.4185 3.67376" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      collaboration: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      pipeline: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      analytics: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 3V21H21" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M7 14L11 10L15 14L21 8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      integration: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M8 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V8M21 8V5C21 4.46957 20.7893 3.96086 20.4142 3.58579C20.0391 3.21071 19.5304 3 19 3H16M16 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V16M3 16V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      assessment: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 5H7C6.46957 5 5.96086 5.21071 5.58579 5.58579C5.21071 5.96086 5 6.46957 5 7V19C5 19.5304 5.21071 20.0391 5.58579 20.4142C5.96086 20.7893 6.46957 21 7 21H17C17.5304 21 18.0391 20.7893 18.4142 20.4142C18.7893 20.0391 19 19.5304 19 19V7C19 6.46957 18.7893 5.96086 18.4142 5.58579C18.0391 5.21071 17.5304 5 17 5H15M9 5C9 5.53043 9.21071 6.03914 9.58579 6.41421C9.96086 6.78929 10.4696 7 11 7H13C13.5304 7 14.0391 6.78929 14.4142 6.41421C14.7893 6.03914 15 5.53043 15 5M9 5C9 4.46957 9.21071 3.96086 9.58579 3.58579C9.96086 3.21071 10.4696 3 11 3H13C13.5304 3 14.0391 3.21071 14.4142 3.58579C14.7893 3.96086 15 4.46957 15 5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    };
    return <span className="talent-feature-icon">{icons[type]}</span>;
  };

  const demoVideoUrl = "https://assets.mixkit.co/videos/preview/mixkit-close-up-of-businesswoman-using-her-laptop-5082-large.mp4";

  return (
    <div className="talent-acquisition-platform">
      {/* Scroll Progress Bar */}
      {/* <div className="talent-scroll-progress" style={{width: `${scrollProgress}%`}}></div> */}

      {/* Video Popup */}
      <VideoPopup
        isOpen={isVideoPopupOpen}
        onClose={closeVideoPopup}
        videoUrl={demoVideoUrl}
      />

      {/* Enhanced Animated Background */}
      

      {/* Talent Navigation Sidebar */}

      {/* <HRCopilotJobStatus/> */}
      {/* <HRCopilotRequirementSection/> */}

      {/* Talent Acquisition Hero Section */}
      <TalentBackground />
      <HRCopilotHero />

      <RecuirementWiseCode />
      <HRCopilotFeatures />
      <HRCopilotNewsSection />








      {/* Talent Acquisition CTA */}

    </div>
  );
};

export default RecruitmentPlatform;